/**
 * Single source of truth for the site's real (crawlable) URLs.
 *
 * The site is one visual page: every route renders the identical scroll and
 * only scrolls to its section (see ScrollToSection). Per-route title /
 * description / canonical are injected at prerender so Search Console treats
 * each URL as a distinct page — without changing anything the user sees.
 *
 * `snippet` is injected into the prerendered body as a visually-hidden
 * paragraph (sr-only) so each URL has unique indexable text beyond the <head>
 * meta tags — preventing Google from consolidating all routes as duplicates.
 *
 * `schema` is injected as a JSON-LD <script> in the <head>, providing
 * per-route structured data signals to Google.
 */
export interface RouteDef {
  path: string;
  /** id of the <section> to scroll to when this route is visited. */
  sectionId: string;
  title: string;
  description: string;
  /** Unique body text injected as sr-only for Google to differentiate this URL. */
  snippet: string;
  /** JSON-LD schema type for this route's structured data. */
  schemaType: string;
}

export const SITE_ORIGIN = 'https://rcrt.cloud';

export const ROUTES: RouteDef[] = [
  {
    path: '/',
    sectionId: 'top',
    title: 'rcrt · Replace your stack. Run on one runtime.',
    description:
      'rcrt · The Business Intelligence Runtime. Agents with persistent memory, sandboxed tools, and connectors to the systems you keep — one governed runtime that replaces the SaaS sprawl.',
    snippet:
      'rcrt is a Business Intelligence Runtime built by possibl.ai, an AI strategy and delivery company based in Auckland and Wellington, New Zealand, serving businesses across New Zealand, Australia, Sydney, Melbourne, and the broader ANZ region. Deploy agents with persistent Level-of-Detail memory, sandboxed tools, human-approved actions, and 300+ routable models — all on one governed, auditable runtime. Replace Salesforce, HubSpot, Notion, and your entire per-seat subscription stack with a single runtime priced on usage, not seats.',
    schemaType: 'WebSite',
  },
  {
    path: '/how-it-works',
    sectionId: 'how',
    title: 'How rcrt works · The Business Intelligence Runtime',
    description:
      'How rcrt works: agents with persistent Level-of-Detail memory, sandboxed tools, human-approved actions, and connectors — assembled on one governed, auditable runtime.',
    snippet:
      'How rcrt works: each agent on the rcrt runtime maintains persistent Level-of-Detail memory — a structured knowledge graph that survives across sessions. Agents are equipped with sandboxed tools (web search, code execution, data connectors) and every action requiring consequence requires human approval before execution. Connectors link to the systems you already use — CRM, calendar, email, databases — without replacing them. The runtime routes prompts across 300+ models and logs every decision for audit.',
    schemaType: 'HowTo',
  },
  {
    path: '/live-demo',
    sectionId: 'demo',
    title: 'rcrt live demo · Watch a CRM assemble itself',
    description:
      'Watch a working CRM assemble itself live on rcrt — agents, tools and memory composing a real app in real time on one runtime.',
    snippet:
      'Watch a live rcrt demo: a fully functional CRM assembles itself in real time as agents with persistent memory, sandboxed tools, and data connectors compose a working application on the rcrt runtime. The demo shows how rcrt agents read from your existing data sources, create structured memory entries, and surface actionable intelligence — all without per-seat licensing or manual configuration.',
    schemaType: 'SoftwareApplication',
  },
  {
    path: '/calculator',
    sectionId: 'calculator',
    title: 'Stack cost calculator · See what rcrt could replace',
    description:
      'Add up the SaaS tools you pay for per seat and see what a single rcrt runtime could replace — priced on what you run, not seats.',
    snippet:
      'Use the rcrt stack cost calculator to total your current per-seat SaaS spend across CRM, BI, project management, and communication tools. Enter your team size and active subscriptions — the calculator shows your annual spend versus what a single rcrt runtime instance would cost, priced on compute and usage rather than per-seat licences. Most teams with 20+ seats see significant savings when consolidating onto one governed runtime.',
    schemaType: 'WebApplication',
  },
  {
    path: '/whats-shipping',
    sectionId: 'shipping',
    title: "What's shipping in rcrt · Products on the runtime",
    description:
      'The products shipping on the rcrt runtime today — agents, connectors, and governed tools that consolidate SaaS sprawl into one auditable platform.',
    snippet:
      "What's shipping on the rcrt runtime: the current product release includes persistent-memory agents for sales intelligence, a CRM connector that reads and writes to Salesforce and HubSpot, a governed code-execution sandbox, a semantic search tool over your internal knowledge base, human-approval workflows for high-consequence actions, and a model router that selects from 300+ LLMs based on task type and cost. Additional connectors for calendar, email, and financial data are in active development.",
    schemaType: 'ItemList',
  },
  {
    path: '/sdk',
    sectionId: 'sdk',
    title: 'Build with the rcrt SDK · Typed agent runtime SDK',
    description:
      'Build on rcrt with a typed SDK: agents with persistent memory, sandboxed tools, 300+ routable models, and connectors — one governed runtime, one install.',
    snippet:
      'Build on the rcrt SDK: a typed TypeScript and Python SDK for constructing agents on the rcrt runtime. Define agents with persistent Level-of-Detail memory stores, register sandboxed tools with input/output schemas, configure human-approval gates for sensitive actions, and route completions across 300+ models with a single API call. The SDK ships with connectors for Salesforce, HubSpot, Google Workspace, Notion, and more — one npm or pip install gives you the full governed runtime.',
    schemaType: 'SoftwareApplication',
  },
];
